import { useState } from "react";
import { Copy } from "lucide-react";
import {
  ATTRIBUTION_WINDOWS,
  attributionLabel,
  blendedRoas,
  fmtInt,
  fmtMoneyCents,
  fmtRoas,
  roasRowNote,
  rowRoas,
  untaggedSummary,
  type Attribution,
  type AttributionModel,
  type AttributionRow,
  type AttributionView,
  type Roas,
  type RoasRow,
} from "../../web-analytics";

type Props = {
  attribution: Attribution;
  /** nola_web_roas CALLS nola_web_attribution, so the two tables cannot disagree about one campaign. */
  roas: Roas | null;
  view: AttributionView;
  onView: (next: AttributionView) => void;
  /** The page's own Visitors total (nola_web_stats), shown beside People and never recomputed here. */
  visitors: number;
  filtered: boolean;
};

const MODEL_LABEL: Record<AttributionModel, string> = { first: "First click", last: "Last click" };

/**
 * Which source and campaign earned each conversion (leads, calls and matched
 * jobs), under first- and last-click, and what the Google Ads spend behind it
 * returned. Ported from Sonar's AttributionPanel; its Stripe copy and Meta
 * spend import are dropped (NOLA's spend is the Google Ads sync). The model
 * and window live in the URL, like every other control on the page.
 */
export default function AttributionPanel({ attribution: a, roas, view, onView, visitors, filtered }: Props) {
  const model = view.model;
  const names = roas?.campaign_names ?? {};
  // Revenue is shown once there is some, or once a payload was refused: an
  // unreadable amount must never look the same as no revenue.
  const showRevenue = a.revenue_cents !== 0 || a.revenue_malformed > 0;
  const convs = (row: AttributionRow) => (model === "first" ? row.first_conversions : row.last_conversions);
  const revenue = (row: AttributionRow) => (model === "first" ? row.first_revenue_cents : row.last_revenue_cents);
  const max = Math.max(1, ...a.rows.map(convs), convs(a.unattributed));

  return (
    <section className="panel wa-attr" aria-labelledby="wa-attr-heading">
      <div className="panel-heading wa-attr-heading">
        <div className="wa-bd-title">
          <h2 id="wa-attr-heading">Conversions and attribution</h2>
          <p className="panel-meta">Which sources and campaigns led to leads, calls and booked jobs</p>
        </div>
        <div className="wa-attr-controls">
          <div className="segmented-control compact" role="group" aria-label="Attribution model">
            {(["first", "last"] as const).map((m) => (
              <button key={m} type="button" aria-pressed={m === model} className={m === model ? "active" : ""} onClick={() => onView({ ...view, model: m })}>
                {MODEL_LABEL[m]}
              </button>
            ))}
          </div>
          <div className="segmented-control compact" role="group" aria-label="Lookback window">
            {ATTRIBUTION_WINDOWS.map((w) => (
              <button key={w} type="button" aria-pressed={w === view.window} className={w === view.window ? "active" : ""}
                title={`Credit a click up to ${w} days before the conversion`} onClick={() => onView({ ...view, window: w })}>
                {w} days
              </button>
            ))}
          </div>
        </div>
      </div>

      {a.conversions === 0 ? (
        // An empty state says what would fill it.
        <div className="wa-attr-empty">
          <p className="wa-attr-lead">
            {a.first_conversion_at
              ? "No conversions in this range. This site has recorded them before, so try a wider range."
              : "No conversions recorded on this site yet."}
          </p>
          <p>Form submissions and phone, email and WhatsApp taps are tracked automatically. Jobs matched to a website lead are added when matching runs.</p>
        </div>
      ) : (
        <>
          <div className="wa-attr-stats">
            <Stat label="Conversions" value={fmtInt(a.conversions)} />
            <Stat label={`Attributed (${MODEL_LABEL[model].toLowerCase()})`} value={fmtInt(a.attributed_conversions)} />
            <Stat label="Unattributed" value={fmtInt(a.unattributed_conversions)} caption="No ad click or tag to credit" />
            <Stat label="People" value={fmtInt(a.people)} caption={`Visitors: ${fmtInt(visitors)}`} />
            {showRevenue ? (
              <Stat label="Revenue" value={fmtMoneyCents(a.revenue_cents)} caption={a.revenue_malformed > 0 ? `${fmtInt(a.revenue_malformed)} unreadable` : undefined} />
            ) : (
              <Stat label="Touchpoints" value={fmtInt(a.touchpoints)} caption={filtered ? "Clicks in this range, not filtered" : "Clicks in this range"} />
            )}
          </div>

          {roas && roas.untagged_conversions > 0 && <UntaggedCallout roas={roas} />}

          <div className="wa-attr-table">
            <div className="wa-attr-row wa-attr-head" aria-hidden="true">
              <span className="wa-attr-label">Source · campaign</span>
              <span className={model === "first" ? "wa-attr-num on" : "wa-attr-num"}>First</span>
              <span className={model === "last" ? "wa-attr-num on" : "wa-attr-num"}>Last</span>
              {showRevenue && <span className="wa-attr-money" title={`Revenue credited under ${MODEL_LABEL[model].toLowerCase()}`}>Revenue</span>}
            </div>
            <ul className="wa-attr-list">
              {/* The unattributed bucket is always last and never cut by the row limit. */}
              {[...a.rows, a.unattributed].map((row) => {
                const label = attributionLabel(row, names);
                return (
                  <li key={row.unattributed ? "__unattributed" : `${row.source_dim}:${row.source}:${row.campaign}`}
                    className={row.unattributed ? "wa-attr-row wa-attr-unattributed" : "wa-attr-row"}>
                    <span className="wa-bd-bar" style={{ width: `${(convs(row) / max) * 100}%` }} aria-hidden="true" />
                    <span className="wa-attr-label" title={row.unattributed ? "Conversions with no ad click or UTM tag inside the window" : label}>{label}</span>
                    <span className={model === "first" ? "wa-attr-num on" : "wa-attr-num"}>
                      <span className="sr-only">First click </span>{fmtInt(row.first_conversions)}
                    </span>
                    <span className={model === "last" ? "wa-attr-num on" : "wa-attr-num"}>
                      <span className="sr-only">Last click </span>{fmtInt(row.last_conversions)}
                    </span>
                    {showRevenue && (
                      <span className="wa-attr-money" title={fmtMoneyCents(revenue(row))}>
                        <span className="sr-only">Revenue </span>{fmtMoneyCents(revenue(row))}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            {a.rows_truncated > 0 && (
              <p className="wa-attr-foot">
                {fmtInt(a.rows_truncated)} smaller {a.rows_truncated === 1 ? "campaign is" : "campaigns are"} not shown. Unattributed is never cut.
              </p>
            )}
          </div>

          {a.by_name.length > 0 && (
            <div className="wa-attr-names">
              <span className="wa-attr-names-label">By event</span>
              {a.by_name.map((b) => (
                <span key={b.name}>
                  <span className="wa-attr-name">{b.name}</span> <strong>{fmtInt(b.conversions)}</strong>
                  {showRevenue && b.revenue_cents !== 0 && <> · {fmtMoneyCents(b.revenue_cents)}</>}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {roas && <RoasBlock roas={roas} model={model} filtered={filtered} />}

      <details className="wa-attr-notes">
        <summary>How attribution and ROAS work</summary>
        <div>
          <p>
            A conversion is credited to an ad click or UTM tag up to <strong>{a.window_days} days</strong> before it. First click credits the
            earliest, last click the latest. A conversion with nothing to credit is unattributed; that row is always shown.
          </p>
          <p>Both conversion columns show both models at once. Revenue follows the selected model, so a job&apos;s value moves between rows when you switch.</p>
          <p>
            People ({fmtInt(a.people)}) counts a browser that keeps its id across days. Visitors ({fmtInt(visitors)}) counts a daily hash of address and
            browser. Neither contains the other.
          </p>
          <p>
            Blended ROAS divides revenue from campaigns that have spend by <em>all</em> Google Ads spend in the range, including spend that matched
            nothing here. Spend joins on the campaign ID in utm_campaign; a campaign tagged by its name is matched to its ID.
          </p>
          {filtered && <p>Filters choose which conversions count. The click lookback and the spend are never filtered, so a filtered ROAS is a floor.</p>}
        </div>
      </details>
    </section>
  );
}

function Stat({ label, value, caption }: { label: string; value: string; caption?: string }) {
  return (
    <div className="wa-attr-stat">
      <span className="wa-signal-label">{label}</span>
      <strong>{value}</strong>
      {caption && <span className="wa-attr-caption">{caption}</span>}
    </div>
  );
}

/** Google conversions no spend can reach, and the one setting that fixes them. */
function UntaggedCallout({ roas }: { roas: Roas }) {
  const summary = untaggedSummary(roas.untagged_conversions);
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(roas.suffix_hint);
      setCopied("yes");
    } catch {
      setCopied("failed");
    }
  };
  return (
    <div className="wa-attr-callout" role="note">
      <p>
        <strong>{summary.headline}</strong> {summary.detail} Set the Google Ads account&apos;s final URL suffix to:
      </p>
      <div className="wa-attr-suffix">
        <code>{roas.suffix_hint}</code>
        <button type="button" className="button secondary" onClick={copy}>
          <Copy aria-hidden="true" /> {copied === "yes" ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="wa-attr-caption" aria-live="polite">
        {copied === "failed" ? "Copy failed. Select the text and copy it by hand." : "Google Ads → Admin → Account settings → Tracking → Final URL suffix."}
      </p>
    </div>
  );
}

function RoasBlock({ roas: r, model, filtered }: { roas: Roas; model: AttributionModel; filtered: boolean }) {
  const names = r.campaign_names;
  const revenue = (row: RoasRow) => (model === "first" ? row.first_revenue_cents : row.last_revenue_cents);
  const withSpend = model === "first" ? r.first_revenue_with_spend_cents : r.last_revenue_with_spend_cents;
  const noSpend = model === "first" ? r.first_revenue_no_spend_cents : r.last_revenue_no_spend_cents;

  // No spend has ever synced for this client: every ROAS is undefined, so say what fills it.
  if (r.spend_rows_total === 0) {
    const waiting = r.rows.filter((row) => row.kind === "revenue_only").map((row) => attributionLabel({ ...row, unattributed: false }, names));
    return (
      <div className="wa-roas wa-attr-empty">
        <h3>Ad spend and ROAS</h3>
        <p className="wa-attr-lead">No Google Ads campaign spend has synced for this client yet. It arrives with the client's Google Ads sync (see Ad spend), and each campaign then shows its return on ad spend.</p>
        {waiting.length > 0 && (
          <p>
            {waiting.length === 1 ? "One campaign is" : `${fmtInt(waiting.length)} campaigns are`} already earning conversions here ({waiting.slice(0, 4).join(", ")}
            {waiting.length > 4 ? `, and ${fmtInt(waiting.length - 4)} more` : ""}). Each becomes a ROAS row once its spend syncs.
          </p>
        )}
      </div>
    );
  }

  const maxSpend = Math.max(1, ...r.rows.map((row) => row.spend_cents));
  return (
    <div className="wa-roas">
      <div className="wa-roas-head">
        <h3>Ad spend and ROAS</h3>
        <p className="panel-meta">
          Google Ads spend, {r.from_date} to {r.to_date}
          {r.last_spend_date ? `, synced through ${r.last_spend_date}` : ""}
        </p>
      </div>
      <div className="wa-attr-stats wa-roas-stats">
        <Stat label="Spend" value={fmtMoneyCents(r.spend_cents)} caption={`${fmtInt(r.clicks)} clicks`} />
        <Stat label={`Revenue (${MODEL_LABEL[model].toLowerCase()})`} value={fmtMoneyCents(withSpend)} caption="From campaigns with spend" />
        <Stat label="ROAS (blended)" value={fmtRoas(blendedRoas(r, model))} caption="All spend in the denominator" />
        <Stat
          label="Unmatched spend"
          value={fmtMoneyCents(r.unmatched_spend_cents)}
          caption={r.orphan_spend_cents > 0
            ? `${fmtMoneyCents(r.orphan_spend_cents)} reached no tracked site`
            : `${fmtInt(r.spend_only_campaigns)} ${r.spend_only_campaigns === 1 ? "campaign" : "campaigns"}`}
        />
      </div>

      <div className="wa-attr-table">
        <div className="wa-attr-row wa-roas-row wa-attr-head" aria-hidden="true">
          <span className="wa-attr-label">Campaign</span>
          <span className="wa-attr-money">Spend</span>
          <span className="wa-attr-money">Revenue</span>
          <span className="wa-attr-roas">ROAS</span>
        </div>
        {r.rows.length === 0 ? (
          <p className="wa-attr-foot">Spend has synced, but none falls in this range and no campaign earned a conversion here. Try a wider range.</p>
        ) : (
          <ul className="wa-attr-list">
            {r.rows.map((row) => {
              const label = attributionLabel({ ...row, unattributed: false }, names);
              const note = roasRowNote(row);
              return (
                <li key={`${row.kind}:${row.source}:${row.campaign}`} className={`wa-attr-row wa-roas-row wa-roas-${row.kind}`}>
                  {/* Bar length is spend, the sort key, so the biggest cheque is the longest bar even when it bought nothing. */}
                  <span className="wa-bd-bar" style={{ width: `${(row.spend_cents / maxSpend) * 100}%` }} aria-hidden="true" />
                  <span className="wa-attr-label">
                    <span className="wa-attr-label-text" title={row.source && row.campaign && names[row.source]?.[row.campaign] ? `${label} (ID ${row.campaign})` : label}>{label}</span>
                    {note && <span className="wa-attr-note">{note}</span>}
                    {row.sites_with_traffic > 1 && (
                      <span className="wa-attr-note">Ran across {fmtInt(row.sites_with_traffic)} of this client&apos;s sites; this row counts only this site&apos;s revenue</span>
                    )}
                  </span>
                  <span className="wa-attr-money"><span className="sr-only">Spend </span>{fmtMoneyCents(row.spend_cents)}</span>
                  <span className="wa-attr-money"><span className="sr-only">Revenue </span>{fmtMoneyCents(revenue(row))}</span>
                  <span className="wa-attr-roas" title={row.spend_cents === 0 ? "No spend recorded, so there is nothing to divide by" : undefined}>
                    <span className="sr-only">ROAS </span>{fmtRoas(rowRoas(row, model))}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {r.rows_truncated > 0 && (
          <p className="wa-attr-foot">
            {fmtInt(r.rows_truncated)} smaller {r.rows_truncated === 1 ? "campaign is" : "campaigns are"} not shown. The totals above cover every campaign.
          </p>
        )}
      </div>

      {(noSpend > 0 || r.unattributed_revenue_cents > 0 || r.other_site_spend_cents > 0 || r.orphan_spend_cents > 0 || filtered) && (
        <div className="wa-roas-notes">
          {noSpend > 0 && (
            <p>
              {fmtMoneyCents(noSpend)} of credited revenue came from {fmtInt(r.revenue_only_campaigns)}{" "}
              {r.revenue_only_campaigns === 1 ? "campaign" : "campaigns"} with no spend recorded. It is left out of ROAS rather than inflating it.
            </p>
          )}
          {r.unattributed_revenue_cents > 0 && (
            <p>A further {fmtMoneyCents(r.unattributed_revenue_cents)} came from unattributed conversions and belongs to no campaign.</p>
          )}
          {r.other_site_spend_cents > 0 && (
            <p>
              {fmtMoneyCents(r.other_site_spend_cents)} on {fmtInt(r.other_site_campaigns)} {r.other_site_campaigns === 1 ? "campaign" : "campaigns"} drove
              only this client&apos;s other sites, so it is left out here.
            </p>
          )}
          {r.orphan_spend_cents > 0 && (
            <p>{fmtMoneyCents(r.orphan_spend_cents)} reached no tracked site at all. That is a broken final URL or a missing tag, not a weak campaign.</p>
          )}
          {filtered && <p>Filters narrow the revenue but never the spend, so a filtered ROAS is a floor.</p>}
        </div>
      )}
    </div>
  );
}
